import * as React from "react";
import { cn } from "./cn";

export type SplashLoaderProps = {
  /** Dark background (ink) with a white arc. Defaults to light (paper + ink arc). */
  onDark?: boolean;
  /** Render the "Chốt Ca" wordmark under the mark. Default true. */
  showWordmark?: boolean;
  /** Render the thin indeterminate progress track. Default true. */
  showProgress?: boolean;
  /** Cover the whole screen with a background. Default true. */
  fullscreen?: boolean;
  className?: string;
};

const INK = "#0F0F0F";
const LIME = "#32ffaa";

/**
 * Animated brand splash — "the act of confirming a shift".
 *
 *  1. The ink "C" arc (= Ca / shift) draws itself counter-clockwise.
 *  2. The lime dot (= Chốt / confirmed) drops into the mouth with a spring.
 *  3. A lime ripple radiates out = locked in.
 *  4. Wordmark rises; the dot keeps breathing while data loads.
 *
 * Pure SVG + CSS (no PNG, no JS timers), reuses the exact logo geometry from
 * `Logo`. Respects `prefers-reduced-motion` by rendering the final static mark.
 */
export function SplashLoader({
  onDark = false,
  showWordmark = true,
  showProgress = true,
  fullscreen = true,
  className,
}: SplashLoaderProps) {
  const arcColor = onDark ? "#FFFFFF" : INK;
  const wordColor = onDark ? "#FFFFFF" : INK;

  return (
    <div
      className={cn(
        "cc-splash flex flex-col items-center justify-center gap-7",
        fullscreen && "fixed inset-0 z-[100]",
        fullscreen && (onDark ? "bg-ink" : "bg-paper"),
        className,
      )}
      role="status"
      aria-label="Đang tải"
    >
      <style>{SPLASH_CSS}</style>

      <svg
        className="cc-splash-mark"
        width={132}
        height={132}
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* C arc = the shift, drawn like a pen stroke */}
        <path
          className="cc-splash-arc"
          d="M 139 61 A 55 55 0 1 0 139 139"
          stroke={arcColor}
          strokeWidth="24"
          strokeLinecap="round"
        />
        {/* ripple = the confirmation pulse */}
        <circle
          className="cc-splash-ripple"
          cx="155"
          cy="100"
          r="16"
          fill="none"
          stroke={LIME}
          strokeWidth="4"
        />
        {/* lime dot = "chốt" / confirmed, snaps into the mouth of the C */}
        <circle
          className="cc-splash-dot"
          cx="155"
          cy="100"
          r="16"
          fill={LIME}
        />
      </svg>

      {showWordmark && (
        <div
          className="cc-splash-word font-satoshi text-[26px] font-black leading-none tracking-tight"
          style={{ color: wordColor }}
        >
          Chốt<span style={{ color: LIME }}> Ca</span>
        </div>
      )}

      {showProgress && (
        <div
          className={cn(
            "cc-splash-track h-1 w-32 overflow-hidden rounded-full",
            onDark ? "bg-white/15" : "bg-ink/10",
          )}
        >
          <div
            className="cc-splash-bar h-full w-2/5 rounded-full"
            style={{ background: LIME }}
          />
        </div>
      )}
    </div>
  );
}

// Scoped keyframes. Arc length ≈ 259 (270° of an r=55 circle).
const SPLASH_CSS = `
.cc-splash .cc-splash-arc {
  stroke-dasharray: 260;
  stroke-dashoffset: 260;
  animation: cc-splash-draw .75s cubic-bezier(.4,0,.2,1) forwards;
}
.cc-splash .cc-splash-dot {
  transform-box: fill-box;
  transform-origin: center;
  transform: scale(0);
  opacity: 0;
  animation:
    cc-splash-drop .55s cubic-bezier(.34,1.56,.64,1) .5s forwards,
    cc-splash-breathe 2.2s ease-in-out 1.4s infinite;
}
.cc-splash .cc-splash-ripple {
  transform-box: fill-box;
  transform-origin: center;
  opacity: 0;
  animation: cc-splash-ring .7s ease-out .82s 1;
}
.cc-splash .cc-splash-word {
  opacity: 0;
  transform: translateY(10px);
  animation: cc-splash-rise .5s ease-out .95s forwards;
}
.cc-splash .cc-splash-track { opacity: 0; animation: cc-splash-fade .3s ease-out 1.2s forwards; }
.cc-splash .cc-splash-bar { animation: cc-splash-indet 1.1s ease-in-out 1.3s infinite; }

@keyframes cc-splash-draw { to { stroke-dashoffset: 0; } }
@keyframes cc-splash-drop {
  0%   { transform: scale(0) translateX(14px); opacity: 0; }
  60%  { transform: scale(1.25) translateX(0);  opacity: 1; }
  100% { transform: scale(1)   translateX(0);   opacity: 1; }
}
@keyframes cc-splash-breathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.08); } }
@keyframes cc-splash-ring {
  0%   { opacity: .55; transform: scale(1); }
  100% { opacity: 0;   transform: scale(2.6); }
}
@keyframes cc-splash-rise { to { opacity: 1; transform: translateY(0); } }
@keyframes cc-splash-fade { to { opacity: 1; } }
@keyframes cc-splash-indet {
  0%   { transform: translateX(-120%); }
  100% { transform: translateX(330%); }
}

@media (prefers-reduced-motion: reduce) {
  .cc-splash .cc-splash-arc { stroke-dashoffset: 0; animation: none; }
  .cc-splash .cc-splash-dot { transform: scale(1); opacity: 1; animation: none; }
  .cc-splash .cc-splash-ripple { animation: none; }
  .cc-splash .cc-splash-word { opacity: 1; transform: none; animation: none; }
  .cc-splash .cc-splash-track { opacity: 1; animation: none; }
  .cc-splash .cc-splash-bar { animation: cc-splash-indet 1.1s ease-in-out infinite; }
}
`;
